import {useMemo} from 'react';
import {view} from '@sail/ui';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {useSelector, setValue} from 'src/v2/state/store';
import {Text} from 'src/v2/components/common/Text';
import {BrandedButton} from 'src/v2/components/common/BrandedButton';
import {Select} from 'src/v2/components/common/Select';
import {FormTextField} from 'src/v2/components/anyCrypto/FormTextField';
import {getNetworkName} from 'src/components/Wallet/constants';
import {isValidCryptoAddress} from '@stripe-internal/crypto-utility';
import {COLORS} from 'src/v2/util/color';
import {useUpdateRefundAddress} from 'src/v2/components/directDeposit/useUpdateRefundAddress';
import type {RefundMethod} from 'src/v2/state/types';

const messages = defineMessages({
  refundMethodLabel: {
    id: 'crypto.payins.v2.refund.refund_method_label',
    description: 'Label for refund method selector',
    defaultMessage: 'Preferred refund method',
  },
  returnToSender: {
    id: 'crypto.payins.v2.refund.return_to_sender',
    description: 'Option to return refund to sender address',
    defaultMessage: 'Return to sender',
  },
  setNewRefundAddress: {
    id: 'crypto.payins.v2.refund.set_new_refund_address',
    description: 'Option to set a new refund address',
    defaultMessage: 'Set a new refund address',
  },
  helperText: {
    id: 'crypto.payins.v2.refund.helper_text',
    description: 'Helper text for refund method selector',
    defaultMessage: "Set a refund address if you're paying from an exchange",
  },
  refundAddressLabel: {
    id: 'crypto.payins.v2.refund.refund_address_label',
    description: 'Label for refund wallet address input',
    defaultMessage: 'Refund wallet address',
  },
  refundAddressPlaceholder: {
    id: 'crypto.payins.v2.refund.refund_address_placeholder',
    description: 'Placeholder for refund wallet address input',
    defaultMessage: 'Enter your refund address',
  },
  refundNetworkLabel: {
    id: 'crypto.payins.v2.refund.refund_network_label',
    description: 'Label showing which network the refund address is for',
    defaultMessage: 'Refund network: {networkName}',
  },
  invalidAddress: {
    id: 'crypto.payins.v2.refund.invalid_address',
    description: 'Error message for invalid crypto address',
    defaultMessage: 'Enter a valid {network} address',
  },
  continueButton: {
    id: 'crypto.payins.v2.refund.continue',
    description: 'Continue button on refund screen',
    defaultMessage: 'Continue',
  },
});

export const RefundScreen = () => {
  const intl = useIntl();
  const [updateRefundAddress, {loading}] = useUpdateRefundAddress();

  const selectedNetwork = useSelector(
    (state) => state.anyCryptoDeposit.selectedNetwork,
  );
  const refundMethod = useSelector(
    (state) => state.anyCryptoDeposit.refundMethod,
  );
  const customRefundAddress = useSelector(
    (state) => state.anyCryptoDeposit.customRefundAddress,
  );

  const refundMethodItems = useMemo(
    () => [
      {
        id: 'sendToPaymentAddress',
        label: intl.formatMessage(messages.returnToSender),
      },
      {
        id: 'setNewRefundAddress',
        label: intl.formatMessage(messages.setNewRefundAddress),
      },
    ],
    [intl],
  );

  const handleRefundMethodChange = (id: string) => {
    setValue((state) => {
      state.anyCryptoDeposit.refundMethod = id as RefundMethod;
      state.anyCryptoDeposit.customRefundAddress = '';
    });
  };

  const handleRefundAddressChange = (value: string) => {
    setValue((state) => {
      state.anyCryptoDeposit.customRefundAddress = value;
    });
  };

  const isAddressValid =
    !customRefundAddress.trim().length ||
    !selectedNetwork ||
    isValidCryptoAddress(customRefundAddress.trim(), selectedNetwork);

  const addressError =
    customRefundAddress.trim().length > 0 && selectedNetwork && !isAddressValid
      ? intl.formatMessage(messages.invalidAddress, {
          network: getNetworkName(selectedNetwork),
        })
      : undefined;

  const isButtonDisabled =
    refundMethod === 'setNewRefundAddress' &&
    (customRefundAddress.trim().length === 0 || !!addressError);

  const handleContinue = async () => {
    if (!selectedNetwork) return;

    const refundAddr =
      refundMethod === 'setNewRefundAddress'
        ? customRefundAddress.trim()
        : null;

    // TODO: handle error from updateRefundAddress
    await updateRefundAddress({
      refundAddress: refundAddr,
      destinationNetwork: selectedNetwork,
    });

    setValue((state) => {
      state.screen = 'ANY_CRYPTO_DEPOSIT';
    });
  };

  return (
    <view.div css={{stack: 'y', gap: 'large'}}>
      <view.div css={{stack: 'y', gap: 'xsmall'}}>
        <Select
          items={refundMethodItems}
          selectedItemId={refundMethod}
          onItemSelect={handleRefundMethodChange}
          label={intl.formatMessage(messages.refundMethodLabel)}
          size="large"
          variant="form"
        />
        {refundMethod === 'sendToPaymentAddress' ? (
          <Text type="caption" css={{color: COLORS.textSubduedOnLight}}>
            <FormattedMessage {...messages.helperText} />
          </Text>
        ) : null}
      </view.div>

      {refundMethod === 'setNewRefundAddress' && selectedNetwork ? (
        <view.div css={{stack: 'y', gap: 'xsmall'}}>
          <FormTextField
            label={<FormattedMessage {...messages.refundAddressLabel} />}
            placeholder={intl.formatMessage(messages.refundAddressPlaceholder)}
            value={customRefundAddress}
            onChange={handleRefundAddressChange}
            error={addressError}
          />
          <Text type="caption" css={{color: COLORS.textDisabled}}>
            <FormattedMessage
              {...messages.refundNetworkLabel}
              values={{networkName: getNetworkName(selectedNetwork)}}
            />
          </Text>
        </view.div>
      ) : null}

      <BrandedButton
        onClick={handleContinue}
        disabled={isButtonDisabled || loading}
        loading={loading}
      >
        <FormattedMessage {...messages.continueButton} />
      </BrandedButton>
    </view.div>
  );
};
